import { PlansScreen } from '../../../../../analytics/plansEvents'
import { useRef } from 'react'
import type { LumpsumRebalanceBuyPlanRecommendationDetails } from '../../../api/rebalanceRecommendation.types'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import type { FundMissedGainsInfoParams } from '../../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { buildRebalanceBuyRecommendationPair } from '../../../pair/buildPlanRecommendationPair'
import {
  PlanTaxLiability,
  areAllSellActionsCompleted
} from '../../../shared/tax/PlanTaxLiability'
import { ReasonsToRebalanceAccordion } from '../../../shared/reasons/ReasonsToRebalanceAccordion'
import { StepsToRebalanceAccordion } from '../../../shared/steps/StepsToRebalanceAccordion'
import { PlanUnderlineTabs } from '../../../shared/sheet/PlanUnderlineTabs'
import { useScrollToFirstAccordionOnTabChange } from '../../../shared/sheet/planRecommendationSheetScroll'

type RebalanceBuyPlanRecommendationBodyProps = {
  planDetails: LumpsumRebalanceBuyPlanRecommendationDetails | null
  selectedSellIndex: number
  onSelectSellIndex: (selectedIndex: number) => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
}

export function RebalanceBuyPlanRecommendationBody({
  planDetails,
  selectedSellIndex,
  onSelectSellIndex,
  onMissedGainsInfoClick
}: RebalanceBuyPlanRecommendationBodyProps) {
  const sellFunds = planDetails?.fundsToBeSold ?? []
  const stickyTabsRef = useRef<HTMLDivElement>(null)
  const firstAccordionRef = useRef<HTMLDivElement>(null)
  useScrollToFirstAccordionOnTabChange(stickyTabsRef, firstAccordionRef, selectedSellIndex)

  const isTaxOutside = sellFunds.length <= 1
  const outsideTaxData = planDetails?.consolidatedTax ?? sellFunds[0]?.taxData ?? null
  const areAllSellsCompleted = areAllSellActionsCompleted(sellFunds)
  // 1:1 → root side; 1:m → counter (sell) side
  const detailsTitle = sellFunds.length > 1 ? 'Sell details' : 'Buy details'

  return (
    <>
      <div className="h-[10px] bg-[#F0F4F7] mt-6" />

      <div ref={stickyTabsRef} className="sticky top-0 z-[1] bg-white pt-5">
        <div className="px-5 pb-6 pt-2 text-[18px] font-[500] leading-[130%] text-[#232323]">
          {detailsTitle}
        </div>
        <PlanUnderlineTabs
          analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
          funds={sellFunds.map((fund) => ({
            fundName: fund?.fundName ?? '',
            fundIsin: fund?.isin ?? '',
            mfLogoUrl: fund?.mfLogoUrl ?? ''
          }))}
          activeTabIndex={selectedSellIndex}
          onChange={onSelectSellIndex}
        />
      </div>

      <div ref={firstAccordionRef} aria-hidden />

      {sellFunds.map((sellFund, sellFundIndex) => {
        const pair = buildRebalanceBuyRecommendationPair(planDetails, sellFund)
        if (!pair) {
          return null
        }

        return (
          <div
            key={sellFund?.isin ?? String(sellFundIndex)}
            className={sellFundIndex === selectedSellIndex ? undefined : 'hidden'}
            aria-hidden={sellFundIndex !== selectedSellIndex}
          >
            <ReasonsToRebalanceAccordion
              analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
              pair={pair}
              onMissedGainsInfoClick={onMissedGainsInfoClick}
            />
            <StepsToRebalanceAccordion
              pair={pair}
              analyticsScreen={PlansScreen.LumpsumRebalanceRecommendation}
            />
            {!isTaxOutside ? (
              <PlanTaxLiability
                isOutside={false}
                areAllSellActionsCompleted={areAllSellsCompleted}
                fundName={pair?.exitingFund?.fundName ?? ''}
                fundIsin={pair?.exitingFund?.isin ?? ''}
                tax={{
                  taxData: pair.exitingFund.taxData,
                  withdrawalAmount: pair.exitingFund.sellAmount ?? 0,
                  currentValue: pair.exitingFund.taxData?.currentValue ?? null
                }}
              />
            ) : null}
          </div>
        )
      })}

      {isTaxOutside ? (
        <PlanTaxLiability
          isOutside
          areAllSellActionsCompleted={areAllSellsCompleted}
          fundName={sellFunds[0]?.fundName ?? ''}
          fundIsin={sellFunds[0]?.isin ?? ''}
          tax={{
            taxData: outsideTaxData,
            // Redeem from the sell tab — not root totalTransferAmount (buy-side).
            withdrawalAmount: showAsFormattedRupeeAmount({
              rawAmount: sellFunds[0]?.sellAmount ?? 0
            }).value,
            currentValue: outsideTaxData?.currentValue ?? null
          }}
        />
      ) : null}
    </>
  )
}
